/**
 * RBAC (Role-Based Access Control) Hooks
 * React hooks for permission checking and user management
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userManagementService } from '../lib/auth/user-management-service';
import {
  EnhancedUser,
  EnhancedUserRole,
  EnhancedPermission,
  UserOrganizationMembership,
  RBACService,
  UserInvitation,
  CreateInvitationRequest,
  UsersListRequest,
  AuditLogQuery,
  AuditLogEntry
} from '../lib/auth/rbac-types';

// ============================================================================
// PERMISSION HOOKS
// ============================================================================

/**
 * Hook for checking user permissions
 */
export const usePermissions = (orgId?: string) => {
  const { user, organization } = useAuth();
  const currentOrgId = orgId || organization?.org_id;

  const hasPermission = useCallback((permission: EnhancedPermission): boolean => {
    if (!user || !currentOrgId) return false;

    return RBACService.hasPermission(
      { user: user as EnhancedUser, org_id: currentOrgId },
      permission
    );
  }, [user, currentOrgId]);

  const hasAnyPermission = useCallback((permissions: EnhancedPermission[]): boolean => {
    if (!user || !currentOrgId) return false;

    return RBACService.hasAnyPermission(
      { user: user as EnhancedUser, org_id: currentOrgId },
      permissions
    );
  }, [user, currentOrgId]);

  const hasAllPermissions = useCallback((permissions: EnhancedPermission[]): boolean => {
    if (!user || !currentOrgId) return false;

    return RBACService.hasAllPermissions(
      { user: user as EnhancedUser, org_id: currentOrgId },
      permissions
    );
  }, [user, currentOrgId]);

  const hasRole = useCallback((role: EnhancedUserRole): boolean => {
    if (!user || !currentOrgId) return false;

    const membership = (user as EnhancedUser).organization_memberships?.find(
      m => m.org_id === currentOrgId && m.status === 'active'
    );

    return membership?.role === role;
  }, [user, currentOrgId]);

  const hasAnyRole = useCallback((roles: EnhancedUserRole[]): boolean => {
    if (!user || !currentOrgId) return false;

    const membership = (user as EnhancedUser).organization_memberships?.find(
      m => m.org_id === currentOrgId && m.status === 'active'
    );

    return roles.includes(membership?.role as EnhancedUserRole);
  }, [user, currentOrgId]);

  const getUserPermissions = useCallback((): EnhancedPermission[] => {
    if (!user || !currentOrgId) return [];

    return RBACService.getUserPermissions(user as EnhancedUser, currentOrgId);
  }, [user, currentOrgId]);

  const canAccessResource = useCallback((
    resourceType: 'experiment' | 'deployment' | 'agent_group' | 'report'
  ): boolean => {
    if (!user || !currentOrgId) return false;

    return RBACService.canAccessResource(
      { user: user as EnhancedUser, org_id: currentOrgId },
      resourceType
    );
  }, [user, currentOrgId]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    getUserPermissions,
    canAccessResource,
    isAdmin: hasAnyRole(['super_admin', 'org_admin']),
    isManager: hasAnyRole(['super_admin', 'org_admin', 'org_manager']),
    canManageUsers: hasPermission('users:create'),
    canManageOrganization: hasPermission('organization:manage_settings')
  };
};

/**
 * Hook for managing user list
 */
export const useUsers = (params: UsersListRequest = {}) => {
  const [users, setUsers] = useState<EnhancedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalByRole, setTotalByRole] = useState<Record<EnhancedUserRole, number>>({} as any);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
    has_more: false
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userManagementService.getUsers(params);
      setUsers(response.users);
      setTotalByRole(response.total_by_role);
      setPagination(response.pagination);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const createUser = useCallback(async (userData: any) => {
    try {
      const newUser = await userManagementService.createUser(userData);
      setUsers(prev => [newUser, ...prev]);
      return newUser;
    } catch (err) {
      throw err;
    }
  }, []);

  const updateUser = useCallback(async (userId: string, userData: any) => {
    try {
      const updatedUser = await userManagementService.updateUser(userId, userData);
      setUsers(prev => prev.map(user => user.user_id === userId ? updatedUser : user));
      return updatedUser;
    } catch (err) {
      throw err;
    }
  }, []);

  const suspendUser = useCallback(async (userId: string, orgId: string, reason: string) => {
    try {
      await userManagementService.suspendUser(userId, orgId, reason);
      setUsers(prev => prev.map(user => {
        if (user.user_id === userId) {
          const updatedMemberships = user.organization_memberships.map(m =>
            m.org_id === orgId ? { ...m, status: 'suspended' as const } : m
          );
          return { ...user, organization_memberships: updatedMemberships };
        }
        return user;
      }));
    } catch (err) {
      throw err;
    }
  }, []);

  const activateUser = useCallback(async (userId: string, orgId: string) => {
    try {
      await userManagementService.activateUser(userId, orgId);
      setUsers(prev => prev.map(user => {
        if (user.user_id === userId) {
          const updatedMemberships = user.organization_memberships.map(m =>
            m.org_id === orgId ? { ...m, status: 'active' as const } : m
          );
          return { ...user, organization_memberships: updatedMemberships };
        }
        return user;
      }));
    } catch (err) {
      throw err;
    }
  }, []);

  const refresh = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    totalByRole,
    pagination,
    createUser,
    updateUser,
    suspendUser,
    activateUser,
    refresh
  };
};

/**
 * Hook for managing user invitations
 */
export const useInvitations = (orgId?: string) => {
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userManagementService.getInvitations({ org_id: orgId });
      setInvitations(response.invitations);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch invitations';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const sendInvitation = useCallback(async (request: CreateInvitationRequest) => {
    try {
      const invitation = await userManagementService.sendInvitation(request);
      setInvitations(prev => [invitation, ...prev]);
      return invitation;
    } catch (err) {
      throw err;
    }
  }, []);

  const resendInvitation = useCallback(async (invitationId: string) => {
    try {
      const invitation = await userManagementService.resendInvitation(invitationId);
      setInvitations(prev => prev.map(inv =>
        inv.invitation_id === invitationId ? invitation : inv
      ));
      return invitation;
    } catch (err) {
      throw err;
    }
  }, []);

  const revokeInvitation = useCallback(async (invitationId: string) => {
    try {
      await userManagementService.revokeInvitation(invitationId);
      setInvitations(prev => prev.map(inv =>
        inv.invitation_id === invitationId ? { ...inv, status: 'revoked' } : inv
      ));
    } catch (err) {
      throw err;
    }
  }, []);

  const refresh = useCallback(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  return {
    invitations,
    loading,
    error,
    sendInvitation,
    resendInvitation,
    revokeInvitation,
    refresh
  };
};

/**
 * Hook for audit logging
 */
export const useAuditLog = (params: AuditLogQuery = {}) => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    has_more: false
  });

  const fetchAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userManagementService.getAuditLog(params);
      setEntries(response.entries);
      setPagination(response.pagination);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch audit log';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const logAction = useCallback(async (
    action: string,
    resourceType: string,
    resourceId?: string,
    details?: Record<string, any>
  ) => {
    try {
      await userManagementService.logUserAction(action, resourceType, resourceId, details);
    } catch (err) {
      console.error('Failed to log action:', err);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  return {
    entries,
    loading,
    error,
    pagination,
    logAction,
    refresh
  };
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook for role-based component rendering
 */
export const useRoleGuard = () => {
  const permissions = usePermissions();

  const RoleGuard = useCallback(({
    role,
    roles,
    permission,
    permissions: requiredPermissions,
    fallback = null,
    children
  }: {
    role?: EnhancedUserRole;
    roles?: EnhancedUserRole[];
    permission?: EnhancedPermission;
    permissions?: EnhancedPermission[];
    fallback?: React.ReactNode;
    children: React.ReactNode;
  }) => {
    let hasAccess = false;

    if (role) {
      hasAccess = permissions.hasRole(role);
    } else if (roles) {
      hasAccess = permissions.hasAnyRole(roles);
    } else if (permission) {
      hasAccess = permissions.hasPermission(permission);
    } else if (requiredPermissions) {
      hasAccess = permissions.hasAllPermissions(requiredPermissions);
    }

    return hasAccess ? React.createElement(React.Fragment, {}, children) : React.createElement(React.Fragment, {}, fallback);
  }, [permissions]);

  return { RoleGuard };
};

/**
 * Hook for organization context
 */
export const useOrganizationContext = () => {
  const { user, organization } = useAuth();

  const userOrganizations = useMemo(() => {
    return (user as EnhancedUser)?.organization_memberships?.filter(m => m.status === 'active') || [];
  }, [user]);

  const currentMembership = useMemo(() => {
    return userOrganizations.find(m => m.org_id === organization?.org_id);
  }, [userOrganizations, organization]);

  const switchOrganization = useCallback(async (orgId: string) => {
    // This would typically call the auth context's switch organization method
    // For now, just log the action
    console.log('Switching to organization:', orgId);
  }, []);

  return {
    userOrganizations,
    currentMembership,
    currentRole: currentMembership?.role,
    switchOrganization,
    canSwitchOrganization: userOrganizations.length > 1
  };
};

export default {
  usePermissions,
  useUsers,
  useInvitations,
  useAuditLog,
  useRoleGuard,
  useOrganizationContext
};